import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-routing-machine';
import 'leaflet-routing-machine/dist/leaflet-routing-machine.css';
import { DealerLocation, TechnicianRoute } from '../types';
import { useTheme } from '@/contexts/ThemeContext';
import { calculateDistance } from '../utils/routeOptimization';
import { env } from '@/config/environment';
import { Navigation } from 'lucide-react';
import { NavigationDirectionsDialog, RouteInstructions } from './NavigationDirectionsDialog';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as unknown as { _getIconUrl: unknown })._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});

interface RouteWithDirectionsProps {
  dealerLocations: DealerLocation[];
  routes: TechnicianRoute[];
  showRouteLines?: boolean;
  selectedRouteId?: string;
  baseLocation?: { lat: number; lng: number } | null;
  onRouteUpdate?: (route: TechnicianRoute, distance: number, duration: number) => void;
}

/** Vehix / recall stops — brand primary (matches --primary / brand.primary.500) */
const ROUTE_STOP_COLOR_VEHIX = '#d4854a';
/** Manual stops — brand accent (complementary sky; matches brand.accent.500) */
const ROUTE_STOP_COLOR_MANUAL = '#0ea5e9';
/** Start / home — warm neutral (brand.neutral.700); distinct from manual sky and route polyline blue */
const ROUTE_HOME_COLOR = '#44403c';

// Custom marker icon - circular numbered marker only (label added via tooltip)
// `color` overrides the default fill (used to tint stops by technician on the
// multi-route board). Manual stops keep an accent border so they stay
// recognizable even when the fill is a technician color.
const createStopIcon = (stopNumber: number, isManualStop: boolean, color?: string) => {
  const backgroundColor =
    color || (isManualStop ? ROUTE_STOP_COLOR_MANUAL : ROUTE_STOP_COLOR_VEHIX);
  const borderColor = color && isManualStop ? ROUTE_STOP_COLOR_MANUAL : 'white';
  const html = `
    <div style="
      width: 30px;
      height: 30px;
      background-color: ${backgroundColor};
      border: 2px solid ${borderColor};
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: bold;
      font-size: 12px;
      font-family: Arial, sans-serif;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    ">${stopNumber}</div>
  `;

  return L.divIcon({
    html: html,
    className: 'stop-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
};

const createHomeIcon = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 30;
  canvas.height = 30;
  const ctx = canvas.getContext('2d');

  if (ctx) {
    // Draw circle background
    ctx.beginPath();
    ctx.arc(15, 15, 13, 0, 2 * Math.PI);
    ctx.fillStyle = ROUTE_HOME_COLOR;
    ctx.fill();
    ctx.strokeStyle = 'white';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Draw home icon
    ctx.fillStyle = 'white';
    ctx.font = 'bold 12px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('H', 15, 15);
  }

  return new L.Icon({
    iconUrl: canvas.toDataURL(),
    className: 'home-marker',
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });
};

type OrderedStop = TechnicianRoute['stops'][number];

const DIRECTION_CYCLE = ['right', 'top', 'left', 'bottom'] as const;
const DIRECTION_OFFSETS: Record<(typeof DIRECTION_CYCLE)[number], [number, number]> = {
  right: [15, 0],
  top: [0, -15],
  left: [-15, 0],
  bottom: [0, 15]
};

/** Polyline styles: technician color (with white halo) or the default blue scheme. */
function buildLineStyles(color?: string, dimmed?: boolean) {
  if (color) {
    return [
      { color: '#ffffff', opacity: dimmed ? 0.3 : 0.9, weight: 7 }, // white halo for contrast
      { color, opacity: dimmed ? 0.4 : 1, weight: 4 } // technician color
    ];
  }
  return [
    { color: '#1e40af', opacity: 1, weight: 6 }, // Bold blue outline
    { color: '#3b82f6', opacity: 0.9, weight: 4 } // Blue center line
  ];
}

/** Build the LRM `createMarker` callback (extracted to keep the effect simple). */
function makeCreateMarker(opts: {
  orderedStops: OrderedStop[];
  color: string | undefined;
  showTooltips: boolean;
  dimmed: boolean;
  opacity: number;
  collect: (marker: L.Marker) => void;
}) {
  const { orderedStops, color, showTooltips, dimmed, opacity, collect } = opts;
  return (i: number, waypoint: L.Routing.Waypoint): L.Marker => {
    if (i === 0) {
      const homeMarker = L.marker(waypoint.latLng, { icon: createHomeIcon(), opacity });
      collect(homeMarker);
      return homeMarker;
    }
    const stopForMarker = orderedStops[i - 1];
    const dealerName = stopForMarker?.dealer?.name || `Stop ${i}`;
    const isManualStop = stopForMarker?.stop_type === 'manual';
    const marker = L.marker(waypoint.latLng, {
      icon: createStopIcon(i, isManualStop, color),
      opacity
    });
    collect(marker);
    // Permanent name tooltips only for the focused route, to avoid clutter
    // when many technician routes are shown at once.
    if (showTooltips && !dimmed) {
      const direction = DIRECTION_CYCLE[((i - 1) % 4) as 0 | 1 | 2 | 3];
      marker.bindTooltip(dealerName, {
        permanent: true,
        direction,
        offset: DIRECTION_OFFSETS[direction],
        className: 'dealer-name-tooltip'
      });
    }
    return marker;
  };
}

// Mapbox Directions router with retry logic for transient failures
/* eslint-disable @typescript-eslint/no-explicit-any */
const createMapboxRouterWithRetry = (accessToken: string, maxRetries = 3, baseDelay = 2000) => {
  const mapboxRouter = L.Routing.mapbox(accessToken, {
    timeout: 30000,
    profile: 'mapbox/driving'
  });

  // Wrap the route method with retry logic
  const originalRoute = mapboxRouter.route.bind(mapboxRouter);

  mapboxRouter.route = function (waypoints: any, callback: any, context: any, options: any) {
    let attempts = 0;
    let cancelled = false;

    const attemptRoute = () => {
      if (cancelled) return; // Don't retry if cancelled
      attempts++;
      originalRoute(
        waypoints,
        // leaflet-routing-machine typings expect a looser callback shape than Mapbox returns
        ((err: any, routes: any) => {
          if (cancelled) return; // Don't call callback if cancelled
          if (err && attempts < maxRetries) {
            // Exponential backoff: 2s, 4s, 8s
            const delay = baseDelay * Math.pow(2, attempts - 1);
            // eslint-disable-next-line no-console
            console.log(`Routing retry ${attempts}/${maxRetries} in ${delay}ms...`);
            setTimeout(attemptRoute, delay);
          } else {
            // Wrap callback in try-catch to handle cases where the map/control
            // has been destroyed while the async routing request was in progress
            try {
              callback.call(context, err, routes);
            } catch (callbackError) {
              // Silently ignore - the routing control was likely removed
              // while the async request was still pending. This is expected
              // behavior when routes change rapidly.
            }
          }
        }) as any,
        context,
        options
      );
    };

    attemptRoute();

    // Return a cancel function that can be used to abort pending retries
    return {
      cancel: () => {
        cancelled = true;
      }
    };
  };

  return mapboxRouter;
};
/* eslint-enable @typescript-eslint/no-explicit-any */

// calculateDistance imported from ../utils/routeOptimization

// Component that manages the routing control
export const RoutingControl: React.FC<{
  route: TechnicianRoute;
  baseLocation: { lat: number; lng: number } | null;
  onRouteUpdate?: (distance: number, duration: number) => void;
  onInstructionsUpdate?: (instructions: RouteInstructions | null) => void;
  /** Polyline + stop-marker color. Defaults to the brand blue/orange scheme. */
  color?: string;
  /** Dim this route (used when another technician is focused on the board). */
  dimmed?: boolean;
  /** Show permanent dealer-name tooltips. Off for dimmed/background routes. */
  showTooltips?: boolean;
}> = React.memo(
  ({
    route,
    baseLocation,
    onRouteUpdate,
    onInstructionsUpdate,
    color,
    dimmed,
    showTooltips = true
  }) => {
    const map = useMap();
    const routingControlRef = useRef<L.Routing.Control | null>(null);
    const routeIdRef = useRef<string>('');
    // Markers created for this route (for imperative dimming without recreating
    // the control — recreating would re-fetch directions on every focus change).
    const markersRef = useRef<L.Marker[]>([]);
    const dimmedRef = useRef<boolean>(!!dimmed);

    useEffect(() => {
      // Generate a stable route ID for comparison - include baseLocation to trigger recalc when it changes.
      // IMPORTANT: key on the stop *sequence* (ordered, not sorted) so drag-to-reorder
      // of the same dealer set still redraws the path. Color also recreates the line.
      const baseLocationKey = baseLocation
        ? `${baseLocation.lat.toFixed(4)}-${baseLocation.lng.toFixed(4)}`
        : 'none';
      const orderedStopKey = [...route.stops]
        .sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0))
        .map(s => s.dealer.id)
        .join('>');
      const colorKey = color || 'default';
      const routePrefix = `${route.technician_id}-${route.stops.length}-${baseLocationKey}`;
      const currentRouteId = `${routePrefix}-${colorKey}-${orderedStopKey}`;

      // Capture previous route ID for cleanup
      const previousRouteId = routeIdRef.current;

      // If route hasn't changed, don't recreate
      if (previousRouteId === currentRouteId && routingControlRef.current) {
        return;
      }

      // Prevent creation if invalid conditions
      if (!map || !baseLocation || route.stops.length === 0) {
        // Clear route if conditions aren't met
        if (routingControlRef.current) {
          try {
            map?.removeControl(routingControlRef.current);
          } catch (error) {
            // Ignore cleanup errors
          }
          routingControlRef.current = null;
          routeIdRef.current = '';
        }
        return;
      }

      routeIdRef.current = currentRouteId;

      // Validate base location
      if (isNaN(baseLocation.lat) || isNaN(baseLocation.lng)) {
        // eslint-disable-next-line no-console
        console.warn('Invalid base location coordinates:', baseLocation);
        return;
      }

      // Filter valid stops and sort by stored stop_order (single source of truth from technician_routes)
      // The route order should be changed via the UI and persisted with "Assign" button,
      // not re-optimized at display time
      const orderedStops = route.stops
        .filter(
          stop =>
            stop.dealer.latitude &&
            stop.dealer.longitude &&
            !isNaN(stop.dealer.latitude) &&
            !isNaN(stop.dealer.longitude)
        )
        .sort((a, b) => (a.stop_order || 0) - (b.stop_order || 0));

      // Build waypoints: start from base location, through optimized stops
      const waypoints: L.LatLng[] = [
        L.latLng(baseLocation.lat, baseLocation.lng),
        ...orderedStops.map(stop => {
          if (!stop.dealer.latitude || !stop.dealer.longitude) {
            throw new Error('Invalid stop coordinates');
          }
          return L.latLng(stop.dealer.latitude, stop.dealer.longitude);
        })
      ];

      if (waypoints.length < 2) {
        return;
      }

      // Clear any existing routing control first
      // Store the old control to clean up after a delay to allow async operations to complete
      const oldControl = routingControlRef.current;
      if (oldControl && map) {
        routingControlRef.current = null; // Clear ref first to prevent event handlers from using it

        // Remove all event handlers first to prevent them from firing during cleanup
        try {
          (oldControl as { off?: () => void }).off?.();
        } catch (e) {
          // Ignore errors when removing handlers
        }

        // Patch the old control's _clearLines to prevent null reference errors
        // This must be done on the OLD control, not the new one
        try {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const oldLine = (oldControl as any)._line;
          if (oldLine && typeof oldLine._clearLines === 'function' && !oldLine._clearLinesPatched) {
            const originalClearLines = oldLine._clearLines.bind(oldLine);
            oldLine._clearLines = function () {
              try {
                if (this._map && this._map._layers) {
                  originalClearLines();
                }
              } catch {
                // Silently ignore - map was destroyed
              }
            };
            oldLine._clearLinesPatched = true;
          }
        } catch {
          // Ignore patching errors
        }

        // Delay cleanup to allow any pending async routing requests to complete
        setTimeout(() => {
          try {
            if (map && oldControl) {
              // Check if control is still attached to the map before removing
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const mapContainer = (map as any)._container;
              if (mapContainer && mapContainer.parentNode) {
                // Try to remove control, but handle errors gracefully
                try {
                  map.removeControl(oldControl);
                } catch (removeError) {
                  // Control might already be removed - this is acceptable
                  // eslint-disable-next-line no-console
                  console.warn('Error removing routing control (safe to ignore):', removeError);
                }
              }
            }
          } catch (error) {
            // Silently handle - control may already be removed or map destroyed
          }
        }, 100);
      }

      // Reset captured markers for this (re)build
      markersRef.current = [];
      const initialOpacity = dimmedRef.current ? 0.35 : 1;

      try {
        // Create routing control with error handling (leaflet-routing-machine typings lag Mapbox router)
        const routingControl = L.Routing.control({
          waypoints,
          routeWhileDragging: false,
          addWaypoints: false,
          collapsible: false, // Prevents LRM collapse button (we use our own Navigation button)
          createMarker: makeCreateMarker({
            orderedStops,
            color,
            showTooltips,
            dimmed: dimmedRef.current,
            opacity: initialOpacity,
            collect: marker => markersRef.current.push(marker)
          }),
          lineOptions: {
            styles: buildLineStyles(color, dimmedRef.current),
            extendToWaypoints: true,
            missingRouteTolerance: 10
          },
          show: false, // Hide the directions panel
          fitSelectedRoutes: false, // Don't auto-fit bounds (we handle this separately)
          router: createMapboxRouterWithRetry(env.routing.mapboxAccessToken || '', 3, 2000)
        } as L.Routing.RoutingControlOptions);

        // Patch the internal _clearLines method to handle null map gracefully
        // This prevents "Cannot read properties of null (reading 'removeLayer')" errors
        // when the routing machine's async operations complete after the map is destroyed
        const patchClearLines = () => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const line = (routingControl as any)._line;
          if (line && typeof line._clearLines === 'function' && !line._clearLinesPatched) {
            const originalClearLines = line._clearLines.bind(line);
            line._clearLines = function () {
              try {
                // Check if map still exists before trying to clear
                if (this._map && this._map._layers) {
                  originalClearLines();
                }
              } catch {
                // Silently ignore - map was destroyed
              }
            };
            line._clearLinesPatched = true;
          }
        };

        // Try to patch immediately and also after a delay (when _line is created)
        patchClearLines();
        setTimeout(patchClearLines, 100);
        setTimeout(patchClearLines, 500);

        // Add error handler before adding to map
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        routingControl.on('routingerror', (e: any) => {
          // Only handle errors if this is still the current route
          if (routeIdRef.current === currentRouteId && routingControlRef.current) {
            // eslint-disable-next-line no-console
            console.warn('Routing error - falling back to straight lines:', e?.error?.message || e);
            // When routing fails, calculate straight-line distance as fallback
            if (onRouteUpdate) {
              // Calculate approximate distance using Haversine
              let totalDistance = 0;
              for (let i = 0; i < waypoints.length - 1; i++) {
                const a = waypoints[i];
                const b = waypoints[i + 1];
                if (!a || !b) continue;
                totalDistance += calculateDistance(a.lat, a.lng, b.lat, b.lng);
              }
              // Estimate duration at 30mph average
              const estimatedDuration = (totalDistance / 30) * 60;
              onRouteUpdate(totalDistance, estimatedDuration);
            }
          }
        });

        // Listen for route found event
        routingControl.on('routesfound', (e: L.Routing.RoutingResultEvent) => {
          // Only update if this is still the current route and control still exists
          if (routeIdRef.current !== currentRouteId || !routingControlRef.current) return;

          const routes = e.routes;
          if (routes.length > 0) {
            const routeResult = routes[0];
            const summary = routeResult?.summary;
            if (!summary) return;
            // Convert meters to miles and seconds to minutes
            const distanceInMiles = summary.totalDistance * 0.000621371;
            const durationInMinutes = summary.totalTime / 60;

            if (onRouteUpdate && routingControlRef.current) {
              onRouteUpdate(distanceInMiles, durationInMinutes);
            }

            // Capture instructions for navigation panel
            if (onInstructionsUpdate && routeResult.instructions) {
              const routeInstructions: RouteInstructions = {
                instructions: routeResult.instructions.map(inst => ({
                  distance: inst.distance || 0,
                  time: inst.time || 0,
                  text: inst.text || '',
                  type: inst.type || '',
                  road: inst.road || '',
                  direction: inst.direction || ''
                })),
                totalDistance: distanceInMiles,
                totalTime: durationInMinutes,
                stops: orderedStops.map((stop, index) => {
                  const addr = stop.dealer.address || '';
                  const city = stop.dealer.city || '';
                  const region = stop.dealer.region || '';
                  const postalCode = stop.dealer.postal_code || '';
                  const stateZip = postalCode ? `${region} ${postalCode}` : region;
                  return {
                    stopNumber: index + 1,
                    name: stop.dealer.name || `Stop ${index + 1}`,
                    address: `${addr}, ${city}, ${stateZip}`.trim()
                  };
                })
              };
              onInstructionsUpdate(routeInstructions);
            }
          }
        });

        // Add to map with error handling
        if (map) {
          try {
            routingControl.addTo(map);
            routingControlRef.current = routingControl;
          } catch (error) {
            // Handle errors when adding control to map
            // This can happen if the map is being destroyed or control is in invalid state
            // eslint-disable-next-line no-console
            console.warn('Error adding routing control to map (safe to ignore):', error);
            routingControlRef.current = null;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error creating routing control:', error);
      }

      // Cleanup function - runs when dependencies change or component unmounts
      return () => {
        // Only cleanup the previous route if we had one and it's different from current
        if (routingControlRef.current && map && previousRouteId !== currentRouteId) {
          const controlToRemove = routingControlRef.current;
          // Clear ref immediately to prevent event handlers from using it
          routingControlRef.current = null;

          // Remove all event handlers first to prevent them from firing during cleanup
          try {
            (controlToRemove as { off?: () => void }).off?.();
          } catch (e) {
            // Ignore errors when removing handlers
          }

          // Delay cleanup to allow any pending async routing requests to complete
          // This prevents the library's internal _clearLines from trying to access null layers
          setTimeout(() => {
            try {
              if (map && controlToRemove) {
                // Check if map container still exists
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const mapContainer = (map as any)._container;
                if (mapContainer && mapContainer.parentNode) {
                  // Try to remove the control, but catch any errors from the library
                  // The leaflet-routing-machine library may have internal async operations
                  // that try to access layers after removal - this is a known library limitation
                  try {
                    map.removeControl(controlToRemove);
                  } catch (removeError) {
                    // If removal fails, control might already be removed or map destroyed
                    // This is acceptable - we've already cleared the ref so it won't be used
                    // eslint-disable-next-line no-console
                    console.warn(
                      'Error removing routing control in cleanup (safe to ignore):',
                      removeError
                    );
                  }
                }
              }
            } catch (error) {
              // Silently handle cleanup errors - control may already be removed
              // The leaflet-routing-machine library may try to access null layers internally
              // when cleaning up routes during async operations - this is a known library limitation
            }
          }, 500); // Increased timeout to allow more time for async routing operations to complete
        }
      };
    }, [map, route, baseLocation, onRouteUpdate, onInstructionsUpdate, color, showTooltips]);

    // Imperatively dim/undim this route when focus changes — WITHOUT recreating the
    // routing control (which would re-fetch directions on every focus change).
    useEffect(() => {
      dimmedRef.current = !!dimmed;
      const lineOpacity = dimmed ? 0.4 : 1;
      const haloOpacity = dimmed ? 0.3 : 0.9;
      const markerOpacity = dimmed ? 0.35 : 1;

      // Fade markers
      try {
        markersRef.current.forEach(m => m.setOpacity?.(markerOpacity));
      } catch {
        // Markers may not be on the map yet; the next routesfound rebuild applies it.
      }

      // Fade the polyline layers inside LRM's internal _line layer group
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const line = (routingControlRef.current as any)?._line;
        if (line && typeof line.eachLayer === 'function') {
          let isHalo = true;
          line.eachLayer((layer: L.Path & { setStyle?: (s: L.PathOptions) => void }) => {
            if (typeof layer.setStyle === 'function') {
              layer.setStyle({ opacity: isHalo ? haloOpacity : lineOpacity });
              isHalo = false;
            }
          });
        }
      } catch {
        // LRM internals not ready / changed; safe to ignore.
      }
    }, [dimmed]);

    return null;
  }
);

// Map bounds calculator

export const MapBoundsCalculator: React.FC<{
  dealerLocations: DealerLocation[];
  routes: TechnicianRoute[];
  baseLocation?: { lat: number; lng: number } | null;
}> = ({ dealerLocations, routes, baseLocation }) => {
  const map = useMap();

  useEffect(() => {
    const allPoints: L.LatLng[] = [];

    // Add base location if provided
    if (baseLocation && !isNaN(baseLocation.lat) && !isNaN(baseLocation.lng)) {
      allPoints.push(L.latLng(baseLocation.lat, baseLocation.lng));
    }

    // Add dealer locations
    dealerLocations.forEach(dealer => {
      if (
        dealer.latitude &&
        dealer.longitude &&
        !isNaN(dealer.latitude) &&
        !isNaN(dealer.longitude)
      ) {
        allPoints.push(L.latLng(dealer.latitude, dealer.longitude));
      }
    });

    // Add route stops
    routes.forEach(route => {
      route.stops.forEach(stop => {
        if (
          stop.dealer.latitude &&
          stop.dealer.longitude &&
          !isNaN(stop.dealer.latitude) &&
          !isNaN(stop.dealer.longitude)
        ) {
          allPoints.push(L.latLng(stop.dealer.latitude, stop.dealer.longitude));
        }
      });
    });

    if (allPoints.length > 0) {
      const bounds = L.latLngBounds(allPoints);
      if (bounds.isValid()) {
        map.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 13
        });
      }
    }
  }, [dealerLocations, routes, baseLocation, map]);

  return null;
};

// Component to display preview route as dashed polyline

export const RouteWithDirections: React.FC<RouteWithDirectionsProps> = ({
  dealerLocations,
  routes,
  showRouteLines = true,
  selectedRouteId,
  baseLocation,
  onRouteUpdate
}) => {
  const { resolvedTheme } = useTheme();
  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // State for navigation directions dialog
  const [showNavigationDialog, setShowNavigationDialog] = useState(false);
  const [routeInstructions, setRouteInstructions] = useState<RouteInstructions | null>(null);

  // Set dark mode background on Leaflet container - persistent monitoring
  useEffect(() => {
    if (!mapRef.current) return;

    const container = mapRef.current.getContainer();
    if (!container) return;

    // Get computed background color from CSS variable
    const root = document.documentElement;
    const bgValue = getComputedStyle(root).getPropertyValue('--background').trim();
    const bgColor = resolvedTheme === 'dark' ? `hsl(${bgValue})` : 'transparent';

    // Function to set background - more aggressive
    const setBackground = () => {
      if (container) {
        // Set on container
        container.style.setProperty('background-color', bgColor, 'important');
        container.style.setProperty('background', bgColor, 'important');

        // Also set on all child panes
        const panes = container.querySelectorAll('.leaflet-pane');
        panes.forEach((pane: Element) => {
          (pane as HTMLElement).style.setProperty('background-color', bgColor, 'important');
          (pane as HTMLElement).style.setProperty('background', bgColor, 'important');
        });
      }
    };

    // Set immediately
    setBackground();

    // Set up MutationObserver to watch for style changes
    const observer = new MutationObserver(() => {
      setBackground();
    });

    observer.observe(container, {
      attributes: true,
      attributeFilter: ['style', 'class'],
      subtree: false
    });

    // Also set up interval as backup (check every 500ms)
    const interval = setInterval(setBackground, 500);

    // Listen to map events that might trigger style changes
    const map = mapRef.current;
    const handleMapEvent = () => {
      setTimeout(setBackground, 100);
    };

    map.on('tileload', handleMapEvent);
    map.on('moveend', handleMapEvent);
    map.on('zoomend', handleMapEvent);

    return () => {
      observer.disconnect();
      clearInterval(interval);
      map.off('tileload', handleMapEvent);
      map.off('moveend', handleMapEvent);
      map.off('zoomend', handleMapEvent);
    };
  }, [resolvedTheme]);

  // Calculate center point
  const calculateMapCenter = (): [number, number] => {
    if (baseLocation && !isNaN(baseLocation.lat) && !isNaN(baseLocation.lng)) {
      return [baseLocation.lat, baseLocation.lng];
    }

    const allLats: number[] = [];
    const allLngs: number[] = [];

    dealerLocations.forEach(dealer => {
      if (
        dealer.latitude &&
        dealer.longitude &&
        !isNaN(dealer.latitude) &&
        !isNaN(dealer.longitude)
      ) {
        allLats.push(dealer.latitude);
        allLngs.push(dealer.longitude);
      }
    });

    routes.forEach(route => {
      route.stops.forEach(stop => {
        if (
          stop.dealer.latitude &&
          stop.dealer.longitude &&
          !isNaN(stop.dealer.latitude) &&
          !isNaN(stop.dealer.longitude)
        ) {
          allLats.push(stop.dealer.latitude);
          allLngs.push(stop.dealer.longitude);
        }
      });
    });

    if (allLats.length > 0 && allLngs.length > 0) {
      const avgLat = allLats.reduce((sum, lat) => sum + lat, 0) / allLats.length;
      const avgLng = allLngs.reduce((sum, lng) => sum + lng, 0) / allLngs.length;
      if (!isNaN(avgLat) && !isNaN(avgLng)) {
        return [avgLat, avgLng];
      }
    }

    // Fallback to Florida center
    return [27.9944024, -81.7602544];
  };

  const mapCenter = calculateMapCenter();

  // Get the selected route
  const selectedRoute = routes.find(r => r.technician_id === selectedRouteId);

  // Validate map center before rendering
  if (!mapCenter || mapCenter.some(coord => isNaN(coord))) {
    // eslint-disable-next-line no-console
    console.warn('Invalid map center coordinates, using fallback');
    return (
      <div className='h-full w-full flex items-center justify-center bg-muted/20'>
        <div className='text-center'>
          <p className='text-sm text-muted-foreground'>
            Unable to display route - invalid coordinates
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className='h-full w-full bg-background dark:bg-background route-preview-map'
      style={{ position: 'relative' }}
    >
      {/* Tooltip styles for dealer name labels */}
      <style>{`
        .dealer-name-tooltip {
          background-color: rgba(255, 255, 255, 0.95) !important;
          padding: 3px 8px !important;
          border-radius: 4px !important;
          font-size: 11px !important;
          font-weight: 600 !important;
          color: #1f2937 !important;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2) !important;
          white-space: nowrap !important;
          font-family: Arial, sans-serif !important;
          border: none !important;
        }
        .dealer-name-tooltip::before {
          display: none !important;
        }
      `}</style>
      <MapContainer
        center={mapCenter}
        zoom={10}
        className='h-full w-full'
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        zoomControl={true}
        scrollWheelZoom={true}
        whenReady={() => {
          const map = mapRef.current;
          if (!map) return;
          const container = map.getContainer();
          if (container) {
            const root = document.documentElement;
            const bgValue = getComputedStyle(root).getPropertyValue('--background').trim();
            const bgColor = resolvedTheme === 'dark' ? `hsl(${bgValue})` : 'transparent';
            container.style.setProperty('background-color', bgColor, 'important');
            container.style.setProperty('background', bgColor, 'important');

            setTimeout(() => {
              container.style.setProperty('background-color', bgColor, 'important');
              container.style.setProperty('background', bgColor, 'important');
            }, 100);
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
        />

        <MapBoundsCalculator
          dealerLocations={dealerLocations}
          routes={routes}
          baseLocation={baseLocation ?? null}
        />

        {/* Routing control for selected route */}
        {showRouteLines && selectedRoute && baseLocation && (
          <RoutingControl
            route={selectedRoute}
            baseLocation={baseLocation}
            onRouteUpdate={(distance, duration) => {
              if (onRouteUpdate) {
                onRouteUpdate(selectedRoute, distance, duration);
              }
            }}
            onInstructionsUpdate={setRouteInstructions}
          />
        )}
      </MapContainer>

      {/* Navigation button - top right corner of map */}
      <div className='absolute top-3 right-3 z-[1000]'>
        <button
          onClick={() => setShowNavigationDialog(true)}
          disabled={!routeInstructions}
          title='View turn-by-turn directions'
          className='p-1 disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transition-transform'
        >
          <Navigation className='h-6 w-6 text-gray-700 drop-shadow-md' />
        </button>
      </div>

      {/* Navigation directions dialog */}
      <NavigationDirectionsDialog
        open={showNavigationDialog}
        onOpenChange={setShowNavigationDialog}
        instructions={routeInstructions}
        route={selectedRoute || null}
      />
    </div>
  );
};

export default RouteWithDirections;
